import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { firmFromQS } from "@/lib/nav";
import { useUrlState } from "@/lib/useUrlState";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TierBadge, PocChip, PocChipPair, UsersCell } from "@/components/Badges";
import { FirmSubmeta } from "@/components/FirmSubmeta";
import { Phone, MagnifyingGlass, CaretRight, ChatTeardropDots, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import { FOLLOWUP_COLUMNS } from "@/lib/exportColumns";
import FirmRowActions from "@/components/FirmRowActions";
import TaskFormDialog from "@/components/TaskFormDialog";

const BULK_CAP = 50;

const SECTIONS = [
  {
    key: "urgent",
    label: "Potential Churn",
    subtitle: "30+ days silent · highest risk — call now",
    accent: "text-red-700",
    dot: "bg-red-500",
    border: "border-l-red-500",
    pill: "bg-red-50 text-red-700 border-red-200",
  },
  {
    key: "inactive",
    label: "Inactive",
    subtitle: "15–30 days silent · usage stalled — schedule a check-in",
    accent: "text-orange-700",
    dot: "bg-orange-500",
    border: "border-l-orange-500",
    pill: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    key: "checkin",
    label: "At Risk",
    subtitle: "8–14 days silent · light touch — proactive call",
    accent: "text-amber-700",
    dot: "bg-amber-500",
    border: "border-l-amber-500",
    pill: "bg-amber-50 text-amber-700 border-amber-200",
  },
];

/** Tenure split mirrors backend risk_rules.py: ≤ 365 days since activation
 *  is "New Customer"; older is "Old Customer". Returns "—" if no activation
 *  date is recorded (unlikely but defensive). */
function tenureLabel(activationDate) {
  if (!activationDate) return "—";
  const act = new Date(activationDate);
  if (isNaN(act.getTime())) return "—";
  const days = Math.floor((Date.now() - act.getTime()) / 86_400_000);
  return days <= 365 ? "New" : "Old";
}

export default function FollowUps() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isPoc = user?.role === "poc";

  const [data, setData] = useState({ urgent: [], inactive: [], checkin: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useUrlState("search", "");
  const [tier, setTier] = useUrlState("tier", "all");
  const [pocFilter, setPocFilter] = useUrlState("poc", "all");
  const [tenureFilter, setTenureFilter] = useUrlState("tenure", "all");
  const [tierOptions, setTierOptions] = useState([]);
  const [pocOptions, setPocOptions] = useState([]);

  // Bulk-assign selection state.
  // - `selected` keeps subdomain → {subdomain, firm_name} so we can pass the
  //   firm list straight to TaskFormDialog without a re-lookup.
  // - Cleared whenever filters change so we never bulk-assign hidden firms.
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkUsers, setBulkUsers] = useState([]);

  useEffect(() => {
    api.get("/meta/options").then((r) => {
      setTierOptions(r.data.tiers);
      setPocOptions(r.data.pocs);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api
      .get("/firms/who-to-call", {
        params: {
          tier: tier !== "all" ? tier : undefined,
          poc: pocFilter !== "all" ? pocFilter : undefined,
        },
      })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [tier, pocFilter]);

  // Drop selection whenever ANY filter changes — prevents bulk-assigning firms
  // the user can no longer see.
  useEffect(() => {
    setSelected({});
  }, [tier, pocFilter, tenureFilter, search]);

  const selectedCount = Object.keys(selected).length;
  const selectedList = useMemo(() => Object.values(selected), [selected]);

  const toggleOne = (r) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[r.subdomain]) {
        delete next[r.subdomain];
        return next;
      }
      if (Object.keys(next).length >= BULK_CAP) {
        toast.error(`Maximum ${BULK_CAP} firms per bulk action.`);
        return prev;
      }
      next[r.subdomain] = { subdomain: r.subdomain, firm_name: r.firm_name };
      return next;
    });
  };

  const toggleSection = (rows, allOn) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (allOn) {
        // Untick every firm in this section
        rows.forEach((r) => { delete next[r.subdomain]; });
        return next;
      }
      // Tick every firm in this section, respecting the global cap
      for (const r of rows) {
        if (next[r.subdomain]) continue;
        if (Object.keys(next).length >= BULK_CAP) {
          toast.error(`Maximum ${BULK_CAP} firms per bulk action — stopped at ${BULK_CAP}.`);
          break;
        }
        next[r.subdomain] = { subdomain: r.subdomain, firm_name: r.firm_name };
      }
      return next;
    });
  };

  const openBulk = async () => {
    if (selectedCount === 0) return;
    // Lazy-load the assignable users list on first open
    if (bulkUsers.length === 0) {
      try {
        const r = await api.get("/tasks/users");
        setBulkUsers(r.data?.users || []);
      } catch (_e) { /* dialog will still open; toast handled inside */ }
    }
    setBulkOpen(true);
  };

  // Client-side search + tenure filter across all three sections
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const matchTenure = (r) => {
      if (tenureFilter === "all") return true;
      const t = tenureLabel(r.activation_date);
      return t === tenureFilter;
    };
    const matchSearch = (r) =>
      !q
      || r.firm_name?.toLowerCase().includes(q)
      || r.subdomain?.toLowerCase().includes(q)
      || r.city?.toLowerCase().includes(q)
      || r.poc?.toLowerCase().includes(q);
    const f = (rows) => (rows || []).filter((r) => matchTenure(r) && matchSearch(r));
    return { urgent: f(data.urgent), inactive: f(data.inactive), checkin: f(data.checkin) };
  }, [data, search, tenureFilter]);

  const total =
    (filtered.urgent?.length || 0)
    + (filtered.inactive?.length || 0)
    + (filtered.checkin?.length || 0);
  const open = (subdomain) => navigate(`/firms/${subdomain}${firmFromQS(location)}`);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-gray-900 flex items-center gap-2">
            <Phone size={26} weight="fill" className="text-gray-700" />
            Follow-ups
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Live firms that need a call this week — auto-scored from Mixpanel activity.
            {isPoc && (
              <span className="ml-1 font-mono text-violet-700">· scoped to POC {user.poc_name}</span>
            )}
          </p>
        </div>
        <Button
          onClick={openBulk}
          disabled={selectedCount === 0}
          className="bg-violet-600 hover:bg-violet-700 text-white shrink-0 disabled:bg-gray-200 disabled:text-gray-400"
          data-testid="followups-bulk-assign-btn"
          title={selectedCount === 0 ? "Tick one or more firms below to enable" : `Assign a task to ${selectedCount} selected firm(s)`}
        >
          <ClipboardText size={16} weight="fill" className="mr-1.5" />
          {selectedCount === 0
            ? "Assign tasks"
            : `Assign tasks (${selectedCount})`}
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search firm, subdomain, city, POC…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="followups-search"
          />
        </div>
        <Select value={tier} onValueChange={setTier}>
          <SelectTrigger className="w-[140px] h-9" data-testid="followups-tier">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tiers</SelectItem>
            {tierOptions.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={pocFilter} onValueChange={setPocFilter}>
          <SelectTrigger className="w-[160px] h-9" data-testid="followups-poc">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All POCs</SelectItem>
            {pocOptions.map((p) => (
              <SelectItem key={p} value={p}>
                {p}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={tenureFilter} onValueChange={setTenureFilter}>
          <SelectTrigger className="w-[140px] h-9" data-testid="followups-tenure">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All tenures</SelectItem>
            <SelectItem value="New">New (≤ 12 months)</SelectItem>
            <SelectItem value="Old">Old (&gt; 12 months)</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-gray-500 ml-auto">
          {loading ? "Loading…" : `${total} firm${total === 1 ? "" : "s"} to call`}
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map((s) => {
        const rows = filtered[s.key] || [];
        const sectionSelectedCount = rows.filter((r) => selected[r.subdomain]).length;
        const allSelected = rows.length > 0 && sectionSelectedCount === rows.length;
        const someSelected = sectionSelectedCount > 0 && sectionSelectedCount < rows.length;
        return (
          <div key={s.key} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`followups-section-${s.key}`}>
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3 flex-wrap">
              {rows.length > 0 && (
                <label
                  className="flex items-center gap-2 pr-2 border-r border-gray-200 cursor-pointer"
                  title={allSelected ? "Untick all in this section" : "Tick every firm in this section"}
                >
                  <Checkbox
                    checked={allSelected ? true : someSelected ? "indeterminate" : false}
                    onCheckedChange={() => toggleSection(rows, allSelected)}
                    data-testid={`followups-section-select-${s.key}`}
                  />
                  <span className="text-[11px] uppercase tracking-wider font-medium text-gray-500">All</span>
                </label>
              )}
              <div className="flex items-center gap-2.5">
                <span className={`w-2.5 h-2.5 rounded-full ${s.dot}`} />
                <span className={`text-xs uppercase tracking-wider font-semibold ${s.accent}`}>
                  {s.label}
                </span>
              </div>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border tabular-nums ${s.pill}`}
                data-testid={`followups-count-${s.key}`}
              >
                {rows.length} {rows.length === 1 ? "firm" : "firms"}
              </span>
              <span className="text-xs text-gray-500">{s.subtitle}</span>
              <div className="ml-auto">
                <ExportButton
                  filename={`followups-${s.key}`}
                  sheetName={s.label}
                  columns={FOLLOWUP_COLUMNS}
                  rows={rows}
                  testId={`export-followups-${s.key}`}
                />
              </div>
            </div>

            {rows.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-gray-400 flex flex-col items-center gap-2">
                <ChatTeardropDots size={20} />
                {s.key === "urgent" ? "No potential-churn firms — well done."
                  : s.key === "inactive" ? "No inactive firms right now."
                  : "No at-risk firms right now."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr className="text-left">
                      <th className="pl-5 pr-2 py-2.5 w-10"></th>
                      <th className="px-5 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Firm</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Users</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Tier</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">POC</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500">Tenure</th>
                      <th className="px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-gray-500 text-right">Silent for</th>
                      <th className="px-2 py-2.5 w-16"></th>
                      <th className="px-4 py-2.5 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const isSelected = !!selected[r.subdomain];
                      return (
                      <tr
                        key={r.subdomain}
                        onClick={() => open(r.subdomain)}
                        className={`border-l-4 ${s.border} border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer transition-colors ${isSelected ? "bg-violet-50/40" : ""}`}
                        data-testid={`followups-row-${r.subdomain}`}
                      >
                        <td
                          className="pl-5 pr-2 py-3 w-10"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleOne(r)}
                            data-testid={`followups-select-${r.subdomain}`}
                            aria-label={`Select ${r.firm_name}`}
                          />
                        </td>
                        <td className="px-5 py-3">
                          <div className="font-medium text-gray-900">{r.firm_name}</div>
                          <FirmSubmeta firm={r} />
                        </td>
                        <td className="px-4 py-3">
                          <UsersCell value={r.num_users} />
                        </td>
                        <td className="px-4 py-3">
                          <TierBadge tier={r.tier} />
                        </td>
                        <td className="px-4 py-3">
                          <PocChipPair poc={r.poc} salesPoc={r.sales_poc} />
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {tenureLabel(r.activation_date)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-mono font-semibold border ${s.pill}`}>
                            {r.days_since_seen ?? "—"}d
                          </span>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap"><FirmRowActions firm={r} /></td>
                        <td className="px-4 py-3 text-right">
                          <CaretRight size={14} className="text-gray-300" />
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      <TaskFormDialog
        open={bulkOpen}
        users={bulkUsers}
        bulkFirms={selectedList}
        onClose={() => setBulkOpen(false)}
        onSaved={() => {
          setBulkOpen(false);
          setSelected({});
        }}
      />
    </div>
  );
}
